import React from 'react'
import { Helmet } from 'react-helmet'
import styled from 'styled-components'
import { ModuleNames } from '../../../constants/componentNames.constants'
import { LocaleCodes } from '../../../constants/localization.constants'
import headerStaticQuery from '../../global/header/headerStaticQuery'
import SEOTags from '../../global/seoTags'
import Layout from '../../layouts/layout'

// Components
import Benefits from '../../global/benefits'
import CollapsibleQuadrant from '../../global/collapsibleQuadrant'
import ContentBlock from '../../global/contentBlock'
import ContentColumns from '../../global/contentColumns'
import ContentRows from '../../global/contentRows'
import Divider from '../../global/divider'
import FeaturedResource from '../../global/featuredResource'
import FeaturedSlider from '../../global/featuredSlider'
import FeaturedTiles from '../../global/featuredTiles'
import FlexibleHero from '../../global/flexibleHero'
import Grid from '../../global/grid'
import Hero from '../../global/hero'
import Icons from '../../global/icons'
import KeyStats from '../../global/keyStats'
import MainCTA from '../../global/mainCta'
import MediaText from '../../global/mediaText'
import ParagraphBlock from '../../global/paragraphBlock'
import PlatformDiagram from '../../global/platformDiagram'
import Quote from '../../global/quote'
import RawHTML from '../../global/rawHTML'
import ResourceRow from '../../global/resourceRow'
import ResourceTabs from '../../global/resourceTabs'
import SliderComponent from '../../global/slider'
import Tables from '../../global/table'
import Tiles from '../../global/tiles'
import VideoSpotlight from '../../global/videoSpotlight'

const rawHTMLPage = ({
  location,
  pageContext,
  path,
  footerData,
  headerData,
  mainNavBannerData,
  featuredSliderData,
}: any) => {
  const { slug, seoMetadata, name, flexSection, footer, header, bodyCss, bodyJs } =
    pageContext?.data

  const headerStaticQueryData = headerData || headerStaticQuery.allContentfulNavMenu()

  const headerInfo = header
    ? {
        allContentfulNavMenu: {
          nodes: [header],
        },
        allContentfulMicrocopySet: headerStaticQueryData?.allContentfulMicrocopySet,
      }
    : null

  const footerInfo = footer
    ? {
        allContentfulNavMenu: {
          nodes: [footer],
        },
      }
    : null

  const defaultLocale = pageContext?.pageLocale || LocaleCodes.ENGLISH.code
  const PageContainer = styled.main`
    ${bodyCss?.bodyCss}
  `

  const dynamicPageContent = flexSection?.map((module: any, i: number) => {
    switch (module?.__typename) {
      case ModuleNames.BENEFITS:
        return <Benefits data={module} key={`page-component--${i}`} />

      case ModuleNames.COLLAPSIBLE_QUADRANT:
        return <CollapsibleQuadrant data={module} key={`page-component--${i}`} />

      case ModuleNames.GLOBAL_CONTENT_BLOCK:
        return <ContentBlock data={module} key={`page-component--${i}`} />

      case ModuleNames.GLOBAL_CONTENT_COLUMNS:
        return <ContentColumns data={module} key={`page-component--${i}`} />

      case ModuleNames.GLOBAL_CONTENT_BLOCK:
        return <ContentBlock data={module} key={`page-component--${i}`} />

      case ModuleNames.CONTENT_ROWS:
        return <ContentRows data={module} key={`page-component--${i}`} />

      case ModuleNames.DIVIDER:
        return <Divider data={module} key={`page-component--${i}`} />

      case ModuleNames.FEATURED_RESOURCE:
        return (
          <FeaturedResource data={module} pageLocale={defaultLocale} key={`page-component--${i}`} />
        )

      case ModuleNames.FLEXIBLE_HERO:
        return <FlexibleHero data={module} key={`page-component--${i}`} />

      case ModuleNames.GRID:
        return <Grid data={module} key={`page-component--${i}`} />

      case ModuleNames.HERO:
        return (
          <Hero
            data={module}
            location={location}
            pageTitle={name}
            showBreadcrumbs={true}
            key={`page-component--${i}`}
            locale={defaultLocale}
          />
        )

      case ModuleNames.ICONS:
        return <Icons data={module} key={`page-component--${i}`} />

      case ModuleNames.GLOBAL_KEY_STATS:
        return <KeyStats data={module} key={`page-component--${i}`} />

      case ModuleNames.GLOBAL_MAIN_CTA:
        return <MainCTA data={module} key={`page-component--${i}`} />

      case ModuleNames.FEATURED_TILES:
        return <FeaturedTiles data={module} key={`page-component--${i}`} />

      case ModuleNames.MEDIA_ASSET:
        return <MediaText data={module} mediaAsset={true} key={`page-component--${i}`} />

      case ModuleNames.MEDIA_TEXT:
        return <MediaText data={module} key={`page-component--${i}`} />

      case ModuleNames.PARAGRAPH_BLOCK:
        return <ParagraphBlock data={module} key={`page-component--${i}`} />

      case ModuleNames.PLATFORM_DIAGRAM:
        return <PlatformDiagram data={module} key={`page-component--${i}`} />

      case ModuleNames.QUOTE:
        return <Quote data={module} key={`page-component--${i}`} />

      case ModuleNames.RAW_HTML:
        return <RawHTML data={module} key={`page-component--${i}`} />

      case ModuleNames.RESOURCE_ROW:
        return <ResourceRow data={module} key={`page-component--${i}`} locale={defaultLocale} />

      case ModuleNames.RESOURCE_TABS:
        return <ResourceTabs data={module} key={`page-component--${i}`} />

      case ModuleNames.SLIDER:
        return <SliderComponent data={module} key={`page-component--${i}`} />

      case ModuleNames.TABLE:
        return <Tables data={module} key={`page-component--${i}`} />

      case ModuleNames.RESOURCE_TABS:
        return <SliderComponent data={module} key={`page-component--${i}`} />

      case ModuleNames.TILES:
        return <Tiles data={module} key={`page-component--${i}`} />

      case ModuleNames.VIDEO_SPOTLIGHT:
        return <VideoSpotlight data={module} key={`page-component--${i}`} />

      default:
        return null
    }
  })

  const pageInner = (
    <PageContainer data-cy={`RawHTMLPage`}>
      {bodyJs && bodyJs.bodyJs && (
        <Helmet>
          <script>{bodyJs.bodyJs}</script>
        </Helmet>
      )}

      <SEOTags seoMetadata={seoMetadata} slug={slug} path={path} locale={defaultLocale} />

      <FeaturedSlider
        location={location}
        featuredSliderData={featuredSliderData}
        slug={slug}></FeaturedSlider>
      {dynamicPageContent}
    </PageContainer>
  )

  return (
    <Layout.Template
      lang={defaultLocale}
      slug={slug}
      headerData={headerInfo}
      footerData={footerInfo}
      showHeader={header !== null}
      showFooter={footer !== null}
      mainNavBannerData={mainNavBannerData}>
      {pageInner}
    </Layout.Template>
  )
}

export default rawHTMLPage
